import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { getAppName } from '../../lib/config';
import { QrCode } from './QrCode';

const CARD =
  'w-full max-w-sm space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900';
const INPUT =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100';
const CODE_INPUT = `${INPUT} tracking-widest`;
const BUTTON =
  'w-full rounded-md bg-slate-900 px-3 py-2 font-medium text-white disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900';
const ERROR = 'text-sm text-red-600 dark:text-red-400';

/**
 * Two-step operator login with TOTP enrollment.
 *  - 'credentials': email + password only (no code field).
 *  - 'totp'       : code-only step for an already-enrolled operator.
 *  - 'enroll'     : QR (client-side) + base32 secret + first-code confirm.
 */
export function LoginForm() {
  const { loginStep } = useAuth();

  return (
    <div
      data-id="em-mgr--auth--login"
      className="flex min-h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950"
    >
      {loginStep === 'totp' ? (
        <TotpPanel />
      ) : loginStep === 'enroll' ? (
        <EnrollPanel />
      ) : (
        <CredentialsPanel />
      )}
    </div>
  );
}

function CredentialsPanel() {
  const { login, error, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void login({ email, password });
  };

  return (
    <form onSubmit={onSubmit} className={CARD}>
      <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{getAppName()}</h1>

      <div className="space-y-1">
        <label htmlFor="mgr-email" className="block text-sm font-medium">
          Email
        </label>
        <input
          id="mgr-email"
          data-id="em-mgr--auth--login--email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={INPUT}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="mgr-password" className="block text-sm font-medium">
          Password
        </label>
        <input
          id="mgr-password"
          data-id="em-mgr--auth--login--password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={INPUT}
        />
      </div>

      {error && (
        <p role="alert" data-id="em-mgr--auth--login--error" className={ERROR}>
          {error}
        </p>
      )}

      <button type="submit" data-id="em-mgr--auth--login--submit" disabled={isLoading} className={BUTTON}>
        {isLoading ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

function TotpPanel() {
  const { submitTotp, error, isLoading } = useAuth();
  const [code, setCode] = useState('');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void submitTotp(code);
  };

  return (
    <form onSubmit={onSubmit} className={CARD}>
      <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        Two-factor authentication
      </h1>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Enter the 6-digit code from your authenticator app.
      </p>

      <div className="space-y-1">
        <label htmlFor="mgr-totp-code" className="block text-sm font-medium">
          Authentication code
        </label>
        <input
          id="mgr-totp-code"
          data-id="em-mgr--auth--totp--code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="123456"
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={CODE_INPUT}
        />
      </div>

      {error && (
        <p role="alert" data-id="em-mgr--auth--totp--error" className={ERROR}>
          {error}
        </p>
      )}

      <button type="submit" data-id="em-mgr--auth--totp--submit" disabled={isLoading} className={BUTTON}>
        {isLoading ? 'Verifying…' : 'Verify'}
      </button>
    </form>
  );
}

function EnrollPanel() {
  const { submitEnroll, enrollment, error, isLoading } = useAuth();
  const [code, setCode] = useState('');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void submitEnroll(code);
  };

  return (
    <form onSubmit={onSubmit} className={CARD}>
      <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        Set up two-factor authentication
      </h1>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Scan this QR code in Google Authenticator (or any TOTP app), then enter the 6-digit code to
        confirm.
      </p>

      {enrollment && (
        <>
          <div
            data-id="em-mgr--auth--enroll--qr"
            className="mx-auto w-48 rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700"
          >
            <QrCode value={enrollment.otpauthUri} label="Scan to enroll two-factor authentication" />
          </div>

          <div className="space-y-1">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Can’t scan? Enter this secret manually:
            </p>
            <code
              data-id="em-mgr--auth--enroll--secret"
              className="block break-all rounded bg-slate-100 px-2 py-1 font-mono text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-200"
            >
              {enrollment.secret}
            </code>
          </div>
        </>
      )}

      <div className="space-y-1">
        <label htmlFor="mgr-enroll-code" className="block text-sm font-medium">
          Enter the 6-digit code from your authenticator to confirm
        </label>
        <input
          id="mgr-enroll-code"
          data-id="em-mgr--auth--enroll--code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="123456"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={CODE_INPUT}
        />
      </div>

      {error && (
        <p role="alert" data-id="em-mgr--auth--enroll--error" className={ERROR}>
          {error}
        </p>
      )}

      <button type="submit" data-id="em-mgr--auth--enroll--submit" disabled={isLoading} className={BUTTON}>
        {isLoading ? 'Confirming…' : 'Confirm & activate'}
      </button>
    </form>
  );
}
